import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetCoachingCenterInvoicesQuery,
} from "../../slices/billingApiSlice";

const CoachingCenterInvoices = () => {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const limit = 20;

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCoachingCenterInvoicesQuery({
    status: status || undefined,
    page,
    limit,
  });

  const invoices = data?.data || [];
  console.log(invoices)
  const pagination = data?.pagination || {
    page: 1,
    limit,
    total: 0,
    pages: 0,
  };

  // ======================================================
  // Helpers
  // ======================================================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getInvoiceNumber = (invoice) => {
    return (
      invoice.invoiceNumber ||
      invoice.invoiceNo ||
      invoice.number ||
      invoice._id
    );
  };

  const getBalance = (invoice) => {
    const total = Number(invoice.total || 0);
    const paidAmount = Number(invoice.paidAmount || 0);

    return Math.max(total - paidAmount, 0);
  };

  const getStatusClasses = (invoiceStatus) => {
    switch (invoiceStatus) {
      case "paid":
        return "bg-green-100 text-green-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      case "pending":
      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  // ======================================================
  // Status Change
  // ======================================================

  const handleStatusChange = (value) => {
    setStatus(value);
    setPage(1);
  };

  // ======================================================
  // Pagination
  // ======================================================

  const handlePrevious = () => {
    if (page > 1) {
      setPage((current) => current - 1);
    }
  };

  const handleNext = () => {
    if (page < pagination.pages) {
      setPage((current) => current + 1);
    }
  };

  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="rounded-xl border bg-white p-8 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />

          <p className="mt-3 text-sm text-gray-500">
            Loading invoices...
          </p>
        </div>
      </div>
    );
  }

  // ======================================================
  // Error
  // ======================================================

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">
          <h3 className="font-semibold text-red-700">
            Unable to load invoices
          </h3>

          <p className="mt-1 text-sm text-red-600">
            Something went wrong while loading your invoices.
          </p>

          <button
            type="button"
            onClick={refetch}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* ==================================================
          Header
      ================================================== */}

      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Invoices
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            View invoices generated for your coaching center.
          </p>
        </div>

        <button
          type="button"
          onClick={refetch}
          disabled={isFetching}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isFetching ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* ==================================================
          Filters
      ================================================== */}

      <div className="flex flex-col gap-4 rounded-xl border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <label
            htmlFor="invoice-status"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Invoice Status
          </label>

          <select
            id="invoice-status"
            value={status}
            onChange={(e) =>
              handleStatusChange(e.target.value)
            }
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All Invoices</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="text-sm text-gray-500">
          Total invoices:{" "}
          <span className="font-semibold text-gray-900">
            {pagination.total}
          </span>
        </div>
      </div>

      {/* ==================================================
          Invoice Table
      ================================================== */}

      <div className="overflow-hidden rounded-xl border bg-white">
        {invoices.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-4xl">📄</div>

            <h3 className="mt-3 font-semibold text-gray-900">
              No invoices found
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              There are no invoices matching the selected filter.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Invoice
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Date
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Subscription
                    </th>

                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Total
                    </th>

                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Paid
                    </th>

                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Balance
                    </th>

                    <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Status
                    </th>

                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {invoices.map((invoice) => {
                    const balance = getBalance(invoice);

                    return (
                      <tr
                        key={invoice._id}
                        className="hover:bg-gray-50"
                      >
                        {/* Invoice */}
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {getInvoiceNumber(invoice)}
                          </div>

                          {invoice._id && (
                            <div className="mt-1 text-xs text-gray-400">
                              ID: {invoice._id}
                            </div>
                          )}
                        </td>

                        {/* Date */}
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                          {formatDate(
                            invoice.invoiceDate ||
                              invoice.createdAt
                          )}
                        </td>

                        {/* Subscription */}
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.subscription?.planName ||
                              "Subscription"}
                          </div>

                          {invoice.subscription?.status && (
                            <div className="mt-1 text-xs text-gray-500">
                              {invoice.subscription.status}
                            </div>
                          )}
                        </td>

                        {/* Total */}
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(invoice.total)}
                        </td>

                        {/* Paid */}
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-green-600">
                          {formatCurrency(
                            invoice.paidAmount
                          )}
                        </td>

                        {/* Balance */}
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(balance)}
                        </td>

                        {/* Status */}
                        <td className="whitespace-nowrap px-6 py-4 text-center">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${getStatusClasses(
                              invoice.status
                            )}`}
                          >
                            {invoice.status}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="whitespace-nowrap px-6 py-4 text-right">
                          <Link
                            to={`/billing/coaching-center/invoices/${invoice._id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ==================================================
                Pagination
            ================================================== */}

            <div className="flex flex-col gap-3 border-t bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-500">
                Page{" "}
                <span className="font-medium text-gray-900">
                  {pagination.page}
                </span>{" "}
                of{" "}
                <span className="font-medium text-gray-900">
                  {pagination.pages || 1}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={page <= 1 || isFetching}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={
                    page >= pagination.pages ||
                    pagination.pages === 0 ||
                    isFetching
                  }
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CoachingCenterInvoices;